import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { runInBackground } from '@/lib/utils';
import { OrganizationAgreementReviewPage } from './OrganizationAgreementReviewPage';
import { useActiveOrganizationSignatureSummary } from './useActiveOrganizationSignatureSummary';

const log = createComponentLogger('OrganizationAgreementSigningPage');

const ReturnToQuerySchema = z.object({
    returnTo: z
        .string()
        .startsWith('/')
        .refine((value) => !value.startsWith('//'), 'returnTo must be a same-origin path')
        .optional(),
});

/**
 * The signing screen a PRINCIPAL is taken to after login while Organization
 * agreements await signature. Signing is not required to use PILLAR, so Sign
 * later returns to where the PRINCIPAL was going.
 */
export function OrganizationAgreementSigningPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const returnTo = useMemo(() => {
        const parseResult = ReturnToQuerySchema.safeParse({
            returnTo: new URLSearchParams(location.search).get('returnTo') ?? undefined,
        });
        return parseResult.success ? (parseResult.data.returnTo ?? '/dashboard') : '/dashboard';
    }, [location.search]);
    const summaryQuery = useActiveOrganizationSignatureSummary();
    const continueToApplication = (): void => {
        runInBackground(navigate(returnTo, { replace: true }), (error: unknown) =>
            log.error('Unable to leave the Organization signing screen', error)
        );
    };

    if (summaryQuery.isLoading) {
        return <p>Loading agreements...</p>;
    }
    const summary = summaryQuery.summary;
    if (summaryQuery.isError) {
        return (
            <div role="alert" className="space-y-3">
                <p>Agreements awaiting signature could not be loaded.</p>
                <div className="flex gap-3">
                    <Button onClick={summaryQuery.refetch}>Retry</Button>
                    <Button variant="outline" onClick={continueToApplication}>
                        Continue
                    </Button>
                </div>
            </div>
        );
    }
    if (summary?.summary_kind !== 'organization' || !summary.can_sign) {
        return (
            <div className="space-y-3">
                <p>There are no agreements for you to sign.</p>
                <Button onClick={continueToApplication}>Continue</Button>
            </div>
        );
    }
    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold">
                        Sign on behalf of {summary.organization_name}
                    </h1>
                    <p className="text-sm text-gray-600">
                        {summary.awaiting_signature_count === 0
                            ? 'Every agreement is signed. Thank you.'
                            : 'You can sign now or later. Payment services are activated after signature.'}
                    </p>
                </div>
                <Button variant="outline" onClick={continueToApplication}>
                    {summary.awaiting_signature_count === 0 ? 'Continue' : 'Sign later'}
                </Button>
            </div>
            <OrganizationAgreementReviewPage
                organizationUuid={summary.organization_uuid}
                organizationName={summary.organization_name}
                allowAcceptance={true}
            />
        </div>
    );
}
